import { SparklesIcon } from '@heroicons/react/24/outline';
import { Switch } from '@main/complykit';
import { useUpdateOrganizationMutation } from '@main/graphql/mutations/UpdateOrganization.admin.generated';
import { useGetOrganizationQuery } from '@main/graphql/queries/GetOrganization.generated';
import { DrawerProperty } from '@main/ui';
import { useUserData } from '@nhost/react';

import { useSetOrganizationAgentsEnabledMutation } from './agents-tab.admin.generated';
import { useGetMainAgentQuery } from './grove-switch.admin.generated';
import { useUpdateOrganizationHandler } from './use-update-org-handler';

export const GroveSwitch = ({ orgId }: { orgId: string }) => {
  const { data } = useGetOrganizationQuery({ orgId });
  const organization = data?.organizations_by_pk;
  const [updateOrganization, { isLoading }] = useUpdateOrganizationMutation();
  const updateOrgHandler = useUpdateOrganizationHandler(orgId);
  const { data: mainAgentData } = useGetMainAgentQuery();
  const mainAgent = mainAgentData?.grove_agents[0];
  const [setAgentsEnabled] = useSetOrganizationAgentsEnabledMutation();
  const user = useUserData();

  if (!organization) {
    return null;
  }

  return (
    <DrawerProperty>
      <DrawerProperty.Label icon={SparklesIcon} id="grove-label" htmlFor="grove">
        Grove (AI agents)
      </DrawerProperty.Label>
      <DrawerProperty.Content px={2} display="flex" alignItems="center">
        <Switch
          id="grove"
          aria-labelledby="grove-label"
          isSelected={organization.is_grove_enabled}
          isPending={isLoading}
          onChange={async () => {
            const enabling = !organization.is_grove_enabled;
            // The main agent acts through an instance row like every other
            // agent; Grove sends its id on every query. Enabling Grove is what
            // creates it. Before the agents app has synced the catalog there
            // is no row to attach to, and the switch must still work.
            if (enabling && mainAgent) {
              await setAgentsEnabled({
                objects: [
                  {
                    agent_id: mainAgent.id,
                    organization_id: orgId,
                    enabled: true,
                    enabled_at: new Date().toISOString(),
                    enabled_by: user?.id ?? null,
                  },
                ],
              }).unwrap();
            }
            updateOrgHandler(
              updateOrganization({
                organization_id: orgId,
                updatePayload: { is_grove_enabled: enabling },
              }),
            );
          }}
        />
      </DrawerProperty.Content>
    </DrawerProperty>
  );
};
